import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, FileText, RefreshCw, Share2, Download, Send, Search, UserCheck, MessageSquare, Mail, Upload, ExternalLink } from 'lucide-react';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './lead-qut.css';
import {
  getQuotes,
  createQuote,
  updateQuote,
  deleteQuote,
  updateQuoteStatus,
  reviseQuote,
  searchApprovers,
  sendToApprover,
  addReviewComment,
  uploadQuotePdf,
  getQuotePdfLink,
  getSystemConfig
} from '../../api';



const STATUS_COLORS = {
  'Draft': '#9CA3AF',
  'Sent': '#F59E0B',
  'Approved': '#10B981',
  'Rejected': '#EF4444',
  'Expired': '#8B5CF6',
  'Cancelled': '#374151',
  'Active': '#10B981',
  'Sent for Review': '#F59E0B',
  'Pending': '#D97706',
  'Inactive': '#6B7280',
  'Pending Approval': '#D97706'
};

const STATUS_ICONS = {
  'Draft': '⚪',
  'Sent': '📤',
  'Approved': '✅',
  'Rejected': '❌',
  'Expired': '⏰',
  'Cancelled': '🚫',
  'Active': '🟢',
  'Sent for Review': '🟠',
  'Pending': '🟡',
  'Inactive': '⚫'
};

const formatNum = (num) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(num || 0);

const formatDatePDF = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (_) {
    return 'N/A';
  }
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch (_) {
    return 'N/A';
  }
};

function QuotesTab({ leadId, leadName, leadPhone = '', leadEmail = '', userRole = 'Owner', currentUserId = '', companyName = 'iXES Solutions', onClose }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareQuoteItem, setShareQuoteItem] = useState(null);
  const [sharePhone, setSharePhone] = useState(leadPhone || '');
  const [shareEmail, setShareEmail] = useState(leadEmail || '');
  const [submitting, setSubmitting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Send to Approver Modal State
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [approverQuoteItem, setApproverQuoteItem] = useState(null);
  const [approverQuery, setApproverQuery] = useState('');
  const [approverSuggestions, setApproverSuggestions] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [searchingApprovers, setSearchingApprovers] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);

  // PDF Viewer State
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Approver Comment State
  const [newComment, setNewComment] = useState('');
const [companyConfig, setCompanyConfig] = useState({
    name: 'Your Company Name',
    address: '123 Business Street, City, State 12345',
    phone: '+91 9876543210',
    email: 'info@yourcompany.com',
    gst: 'GSTIN: 22ABCDE1234F1Z5',
    website: '',
    preparedBy: 'Sales Team',
    logo: '',
    logoFormat: 'PNG'
  });

  // Fetch company config on mount
  useEffect(() => {
    const fetchCompanyConfig = async () => {
      try {
        const response = await getSystemConfig();
        console.log('config', response);
        const configData = response?.data;
        if (configData) {
          setCompanyConfig({
            ...companyConfig,
            address: `${configData?.onboarding?.address?.country || ''}, ${configData?.onboarding?.address?.state || ''}, ${configData?.onboarding?.address?.city || ''}, ${configData?.onboarding?.address?.zip || ''}`,
            email: configData?.onboarding?.email || companyConfig.email,
            gst: configData?.onboarding?.businessData?.registrationNumber || companyConfig.gst,
            logo: configData?.logo || companyConfig.logo,
            name: configData?.tenantName || companyConfig.name,
            phone: configData?.onboarding?.phone || companyConfig.phone,
            website: configData?.onboarding?.businessData?.website || companyConfig.website,
          });
        }
      } catch (error) {
        console.error('Failed to fetch company config:', error);
        // Keep using default config
      }
    };
    fetchCompanyConfig();
  }, []);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    quoteNumber: '',
    leadId: leadId || '',
    leadName: leadName || '',
    project: '',
    amount: 0,
    status: 'Draft',
    validUntil: '',
    items: [{ description: '', qty: 1, rate: 0, tax: 18 }],
    terms: '',
    discount: 0,
    billingAddress: '',
    shippingAddress: '',
    poNumber: '',
    paymentTerms: 'Net 15',
    deliveryDate: ''
  });

  const [isEditing, setIsEditing] = useState(false);

  const isOwner = userRole === 'Owner' || userRole === 'owner';
  const isApprover = userRole === 'Approver' || userRole === 'approver';
  const isClient = userRole === 'Client' || userRole === 'client';

  useEffect(() => {
    fetchQuotes();
  }, [leadId]);

  // Autocomplete search for approvers
  useEffect(() => {
    if (!approverQuery || approverQuery.trim().length < 1) {
      setApproverSuggestions([]);
      setSearchAttempted(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingApprovers(true);
      try {
        const response = await searchApprovers(approverQuery);
        if (response && response.success && Array.isArray(response.data)) {
          setApproverSuggestions(response.data);
        } else {
          setApproverSuggestions([]);
        }
      } catch (err) {
        console.error('Search approvers error:', err);
        setApproverSuggestions([]);
      } finally {
        setSearchingApprovers(false);
        setSearchAttempted(true);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [approverQuery]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await getQuotes(leadId);
      if (response && response.success && Array.isArray(response.data)) {
        setQuotes(response.data);
      } else if (Array.isArray(response)) {
        setQuotes(response);
      } else {
        setQuotes([]);
      }
    } catch (error) {
      console.error('Fetch quotes error:', error);
      toast.error('Failed to fetch quotes from server');
    } finally {
      setLoading(false);
    }
  };

const calculateTotal = () => {
  const subtotal = formData.items.reduce(
    (sum, item) =>
      sum +
      (Number(item.qty || 0) * Number(item.rate || 0)),
    0
  );

  const discountAmount =
    (subtotal * Number(formData.discount || 0)) / 100;

  const taxableAmount = subtotal - discountAmount;

  const tax = formData.items.reduce((sum, item) => {
    const itemQty = Number(item.qty || 0);
    const itemRate = Number(item.rate || 0);
    const itemTax = Number(item.tax || 0);

    const itemTotal = itemQty * itemRate;
    const itemDiscount =
      (itemTotal * Number(formData.discount || 0)) / 100;

    return sum + (
      ((itemTotal - itemDiscount) * itemTax) / 100
    );
  }, 0);

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discountAmount: Math.round(discountAmount * 100) / 100,
    tax: Math.round(tax * 100) / 100,
    total: Math.round((taxableAmount + tax) * 100) / 100
  };
};

  const totals = calculateTotal();

  const handleCreateQuote = async () => {
    if (!formData.project || !formData.validUntil) {
      toast.error('Please fill in required fields (Project and Valid Until)');
      return;
    }

    const invalidItems = formData.items.some(item => !item.description || item.rate <= 0);
    if (invalidItems) {
      toast.error('Please ensure all line items have a description and rate greater than 0');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        leadId: leadId || formData.leadId || 'LD-0000',
        leadName: leadName || formData.leadName || '',
        quoteTitle: formData.project,
        projectService: formData.project,
        validUntil: formData.validUntil,
        status: formData.status || 'Draft',
        items: formData.items.map(item => ({
          description: item.description,
          quantity: Number(item.qty),
          unitPrice: Number(item.rate),
          tax: Number(item.tax) || 18,
          unit: 'pcs',
          discount: 0
        })),
        discountValue: Number(formData.discount) || 0,
        discountType: 'percentage',
        termsAndConditions: formData.terms,
        billingAddress: formData.billingAddress,
        shippingAddress: formData.shippingAddress,
        poNumber: formData.poNumber,
        paymentTerms: formData.paymentTerms,
        deliveryDate: formData.deliveryDate
      };

      const response = await createQuote(payload);
      toast.success(response?.message || 'Quote created successfully!');
      resetForm();
      setShowCreateModal(false);
      await fetchQuotes();
    } catch (error) {
      console.error('Create quote error:', error);
      toast.error(error?.response?.data?.message || 'Failed to create quote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateQuote = async () => {
    if (!selectedQuote) return;

    if (selectedQuote.isActive === false) {
      toast.error('Inactive quotes are read-only history and cannot be edited.');
      return;
    }

    setSubmitting(true);
    try {
      const targetId = (selectedQuote._id && selectedQuote._id !== leadId)
        ? selectedQuote._id
        : (selectedQuote.quoteNumber || selectedQuote._id || selectedQuote.id);
      const payload = {
        quoteTitle: formData.project,
        projectService: formData.project,
        validUntil: formData.validUntil,
        status: formData.status,
        items: formData.items.map(item => ({
          description: item.description,
          quantity: Number(item.qty),
          unitPrice: Number(item.rate),
          tax: Number(item.tax) || 18,
          unit: 'pcs',
          discount: 0
        })),
        discountValue: Number(formData.discount) || 0,
        discountType: 'percentage',
        termsAndConditions: formData.terms,
        billingAddress: formData.billingAddress,
        shippingAddress: formData.shippingAddress,
        poNumber: formData.poNumber,
        paymentTerms: formData.paymentTerms,
        deliveryDate: formData.deliveryDate
      };

      const response = await updateQuote(targetId, payload);
      toast.success(response?.message || 'Quote updated successfully!');
      resetForm();
      setShowCreateModal(false);
      setSelectedQuote(null);
      setIsEditing(false);
      await fetchQuotes();
    } catch (error) {
      console.error('Update quote error:', error);
      toast.error(error?.response?.data?.message || 'Failed to update quote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteQuote = async (quoteId, isActive) => {
    if (isActive === false) {
      toast.error('Inactive quotes are retained for history and cannot be deleted.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this active quote?')) return;

    try {
      await deleteQuote(quoteId);
      toast.success('Quote deleted successfully');
      await fetchQuotes();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete quote');
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuoteStatus(quoteId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      await fetchQuotes();
      if (selectedQuote) {
        setSelectedQuote(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleRevise = async (quoteId) => {
    try {
      await reviseQuote(quoteId);
      toast.success('Revision created as new active quote!');
      await fetchQuotes();
    } catch (error) {
      console.error('Revise error:', error);
      toast.error(error?.response?.data?.message || 'Failed to create revision');
    }
  };

  const handleOpenApproverModal = (quote) => {
    if (quote.isActive === false) {
      toast.error('Inactive quotes cannot be sent to an approver.');
      return;
    }
    setApproverQuoteItem(quote);
    setApproverQuery('');
    setApproverSuggestions([]);
    setSelectedCandidate(null);
    setSearchAttempted(false);
    setShowApproverModal(true);
  };

  const handleSendToApproverSubmit = async () => {
    if (!selectedCandidate || !approverQuoteItem) {
      toast.error('Please select an approver from the autocomplete list');
      return;
    }

    setSubmitting(true);
    try {
      const targetId = approverQuoteItem._id || approverQuoteItem.id;
      const response = await sendToApprover(targetId, {
        approverId: selectedCandidate.id,
        approverName: selectedCandidate.name || selectedCandidate.username,
        approverEmail: selectedCandidate.email
      });

      toast.success(response?.message || `Quote sent for review to ${selectedCandidate.name}`);
      setShowApproverModal(false);
      setApproverQuoteItem(null);
      await fetchQuotes();
    } catch (error) {
      console.error('Send to approver error:', error);
      toast.error(error?.response?.data?.message || 'Failed to send quote to approver');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedQuote) return;
    try {
      const targetId = selectedQuote._id || selectedQuote.id;
      const response = await addReviewComment(targetId, newComment.trim());
      toast.success('Review comment added');
      setNewComment('');
      if (response && response.data) {
        setSelectedQuote(response.data);
      }
      await fetchQuotes();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setShowDetailModal(true);
  };

  const handleOpenShareModal = (quote) => {
    setShareQuoteItem(quote);
    setSharePhone(leadPhone || '');
    setShareEmail(leadEmail || '');
    setShowShareModal(true);
  };

  // Professional PDF Generation Function - Classic Quote Template
// Professional PDF Generation Function - Clean Professional Layout
const generateProfessionalPDF = (quote) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 20;
  let y = margin;

  // Colors
  const primaryColor = [31, 41, 55];
  const accentColor = [79, 70, 229];
  const lightGray = [248, 249, 250];
  const mediumGray = [107, 114, 128];
  const darkGray = [55, 65, 81];
  const white = [255, 255, 255];
  const borderColor = [220, 220, 220];
  const successColor = [16, 185, 129];

  // Helper function for drawing lines
  const drawLine = (x1, y1, x2, y2, color = borderColor, width = 0.2) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(width);
    doc.line(x1, y1, x2, y2);
  };

  // ============ HEADER SECTION ============
  // Layout: a fixed-size LOGO BOX top-left, company name/details to its
  // right, and the QUOTATION title + number + status badge right-aligned
  // on the opposite side of the same row block. Everything below this is
  // anchored to headerBottom so spacing never drifts.

  const logoBoxX = margin;
  const logoBoxY = y;
  const logoBoxW = 34;
  const logoBoxH = 24;
  const textBlockX = logoBoxX + logoBoxW + 8; // company text starts right of logo

  // --- Logo (manual placeholder if none supplied) ---
  if (companyConfig.logo) {
    try {
      doc.addImage(
        companyConfig.logo,
        companyConfig.logoFormat || 'PNG',
        logoBoxX,
        logoBoxY,
        logoBoxW,
        logoBoxH,
        undefined,
        'FAST'
      );
    } catch (e) {
      console.error('Failed to render logo in PDF:', e);
    }
  } else {
    // Dashed placeholder box marking exactly where the logo will sit.
    // Paste a base64 image into companyConfig.logo to replace this.
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.4);
    if (typeof doc.setLineDashPattern === 'function') {
      doc.setLineDashPattern([1.5, 1.5], 0);
    }
    doc.roundedRect(logoBoxX, logoBoxY, logoBoxW, logoBoxH, 1.5, 1.5, 'S');
    if (typeof doc.setLineDashPattern === 'function') {
      doc.setLineDashPattern([], 0);
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...mediumGray);
    doc.text('YOUR LOGO', logoBoxX + logoBoxW / 2, logoBoxY + logoBoxH / 2 - 1, { align: 'center' });
    doc.setFontSize(5.5);
    doc.text('(add manually)', logoBoxX + logoBoxW / 2, logoBoxY + logoBoxH / 2 + 4, { align: 'center' });
  }

  // --- Company name & details (right of logo box) ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...primaryColor);
  doc.text(companyConfig.name, textBlockX, logoBoxY + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...mediumGray);
  const companyLines = doc.splitTextToSize(companyConfig.address, 78);
  let compY = logoBoxY + 11.5;
  companyLines.slice(0, 2).forEach((line) => {
    doc.text(line, textBlockX, compY);
    compY += 4;
  });
  doc.text(`Ph: ${companyConfig.phone}`, textBlockX, compY);
  compY += 4;
  doc.text(`${companyConfig.email}  |  ${companyConfig.website}`, textBlockX, compY);

  // --- Quotation title block (right-aligned) ---
  const qNum = quote.quoteNumber || quote.id || 'QT-000';
  const status = quote.status || 'Draft';
  const statusColor = STATUS_COLORS[status] || '#6B7280';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...accentColor);
  doc.text('QUOTATION', pageWidth - margin, logoBoxY + 6, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...mediumGray);
  doc.text(`# ${qNum}`, pageWidth - margin, logoBoxY + 12, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...white);
  doc.setFillColor(...hexToRgb(statusColor));
  const badgeW = 30;
  doc.roundedRect(pageWidth - margin - badgeW, logoBoxY + 15, badgeW, 7, 1, 1, 'F');
  doc.text(status, pageWidth - margin - badgeW / 2, logoBoxY + 19.5, { align: 'center' });

  // GST line spans full width under the logo/company block for consistency
  const headerBottom = logoBoxY + logoBoxH;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...mediumGray);
  doc.text(`${companyConfig.gst}   |   Prepared by: ${companyConfig.preparedBy}`, margin, headerBottom + 4);

  y = headerBottom + 9;

  // Decorative line
  drawLine(margin, y, pageWidth - margin, y, accentColor, 0.5);
  y += 10;

  // ============ QUOTE INFO GRID ============
  const infoY = y;
  const col1 = margin;
  const col2 = margin + 55;
  const col3 = margin + 110;
  const rowHeight = 8;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...darkGray);

  // Row 1: Date
  doc.text('Date Issued:', col1, infoY);
  doc.text(formatDateLong(quote.createdAt || Date.now()), col2, infoY);
  
  // Row 2: Valid Until
  doc.text('Valid Until:', col1, infoY + rowHeight);
  doc.text(formatDateLong(quote.validUntil), col2, infoY + rowHeight);
  
  // Row 3: Customer ID
  doc.text('Customer ID:', col1, infoY + rowHeight * 2);
  doc.text(quote.leadId || leadId || 'LD-0000', col2, infoY + rowHeight * 2);
  
  // Row 4: PO Number
  // doc.text('PO Number:', col3, infoY);
  // doc.text(quote.poNumber || 'N/A', col3 + 30, infoY);
  
  // // Row 5: Payment Terms
  // doc.text('Payment Terms:', col3, infoY + rowHeight);
  // doc.text(quote.paymentTerms || 'Net 15', col3 + 30, infoY + rowHeight);
  
  // // Row 6: Delivery Date
  // doc.text('Delivery Date:', col3, infoY + rowHeight * 2);
  // doc.text(quote.deliveryDate ? formatDateLong(quote.deliveryDate) : 'N/A', col3 + 30, infoY + rowHeight * 2);

  y = infoY + rowHeight * 3 + 10;

  // ============ DIVIDER ============
  drawLine(margin, y, pageWidth - margin, y, borderColor, 0.3);
  y += 8;

  // ============ CUSTOMER SECTION ============
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...primaryColor);
  doc.text('BILL TO', margin, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...darkGray);
  
  // Customer Name
  doc.text(quote.leadName || leadName || 'Valued Customer', margin, y);
  y += 5;

  // Billing Address
  if (quote.billingAddress) {
    doc.setFontSize(9);
    doc.setTextColor(...mediumGray);
    const addrLines = quote.billingAddress.split('\n');
    addrLines.forEach(line => {
      doc.text(line, margin, y);
      y += 4.5;
    });
  }

  // Customer Contact
  if (leadPhone || leadEmail) {
    y += 2;
    doc.setFontSize(9);
    doc.setTextColor(...mediumGray);
    let contactText = '';
    if (leadPhone) contactText += `Phone: ${leadPhone}`;
    if (leadEmail) contactText += contactText ? ` | Email: ${leadEmail}` : `Email: ${leadEmail}`;
    if (contactText) doc.text(contactText, margin, y);
    y += 5;
  }

  y += 5;
  drawLine(margin, y, pageWidth - margin, y, borderColor, 0.3);
  y += 10;

  // ============ ITEMS TABLE ============
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...primaryColor);
  doc.text('Items / Services', margin, y);
  y += 6;

  const rawItems = Array.isArray(quote.items) && quote.items.length > 0
    ? quote.items
    : [{ description: 'No items', quantity: 1, unitPrice: 0, tax: 0 }];

  // Prepare table data
  const tableHeaders = [
    { title: '#', align: 'center', width: 10 },
    { title: 'Description', align: 'left', width: 68 },
    { title: 'Qty', align: 'center', width: 16 },
    { title: 'Unit Price', align: 'right', width: 28 },
    { title: 'Tax %', align: 'center', width: 16 },
    { title: 'Total', align: 'right', width: 32 }
  ];

  const tableBody = rawItems.map((item, index) => {
    const qty = Number(item.quantity || item.qty || 1);
    const rate = Number(item.unitPrice || item.rate || 0);
    const tax = Number(item.tax || 18);
    const total = qty * rate;
    const taxAmount = total * (tax / 100);
    const grandTotal = total + taxAmount;
    
    return [
      String(index + 1),
      item.description || 'Item Description',
      String(qty),
      `Rs. ${formatNum(rate)}`,
      `${tax}%`,
      `Rs. ${formatNum(grandTotal)}`
    ];
  });

  // Add discount row if applicable
  const discountAmount = Number(quote.discountAmount || 0);
  if (discountAmount > 0) {
    tableBody.push([
      '',
      `Discount (${quote.discountValue || 0}%)`,
      '',
      '',
      '',
      `- Rs. ${formatNum(discountAmount)}`
    ]);
  }

  autoTable(doc, {
    startY: y,
    head: [tableHeaders.map(h => h.title)],
    body: tableBody,
    headStyles: {
      fillColor: [245, 245, 250],
      textColor: darkGray,
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center',
      valign: 'middle',
      lineWidth: 0.1,
      lineColor: borderColor
    },
    bodyStyles: {
      textColor: darkGray,
      fontSize: 8,
      valign: 'middle',
      lineWidth: 0.1,
      lineColor: borderColor
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 68, halign: 'left' },
      2: { cellWidth: 16, halign: 'center' },
      3: { cellWidth: 28, halign: 'right' },
      4: { cellWidth: 16, halign: 'center' },
      5: { cellWidth: 32, halign: 'right' }
    },
    alternateRowStyles: { fillColor: [250, 250, 252] },
    margin: { left: margin, right: margin },
    tableWidth: pageWidth - (margin * 2),
    styles: {
      fontSize: 8,
      cellPadding: 2.5
    }
  });

  y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : y + 40;

  // ============ SUMMARY SECTION ============
  const subtotal = Number(quote.subtotal || quote.amount || 0);
  const taxAmount = Number(quote.taxAmount || 0);
  const grandTotal = Number(quote.grandTotal || quote.amount || 0);
  const discAmount = Number(quote.discountAmount || 0);
  const taxableAmount = subtotal - discAmount;

  // Summary box on the right
  const summaryWidth = 80;
  const summaryX = pageWidth - margin - summaryWidth;
  const summaryY = y;

  // Box background
  doc.setFillColor(...lightGray);
  doc.roundedRect(summaryX, summaryY, summaryWidth, 45, 2, 2, 'F');
  doc.setDrawColor(...borderColor);
  doc.roundedRect(summaryX, summaryY, summaryWidth, 45, 2, 2, 'S');

  const renderSummaryRow = (label, value, yPos, isBold = false, isAccent = false, fontSize = 9) => {
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(...(isAccent ? accentColor : darkGray));
    doc.text(label, summaryX + 6, yPos);
    doc.text(value, summaryX + summaryWidth - 6, yPos, { align: 'right' });
  };

  let sY = summaryY + 6;
  renderSummaryRow('Subtotal:', `Rs. ${formatNum(subtotal)}`, sY);
  sY += 6;
  
  if (discAmount > 0) {
    renderSummaryRow('Discount:', `- Rs. ${formatNum(discAmount)}`, sY);
    sY += 6;
  }
  
  renderSummaryRow('Taxable Amount:', `Rs. ${formatNum(taxableAmount)}`, sY);
  sY += 6;
  
  renderSummaryRow('Tax (GST):', `Rs. ${formatNum(taxAmount)}`, sY);
  sY += 6;

  // Divider
  doc.setDrawColor(...borderColor);
  doc.line(summaryX + 6, sY, summaryX + summaryWidth - 6, sY);
  sY += 5;

  renderSummaryRow('Grand Total:', `Rs. ${formatNum(grandTotal)}`, sY, true, true, 11);

  y = summaryY + 55;

  // ============ TERMS AND CONDITIONS ============
  if (quote.termsAndConditions || quote.terms) {
    const termsText = quote.termsAndConditions || quote.terms;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...primaryColor);
    doc.text('Terms & Conditions', margin, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...mediumGray);
    const splitTerms = doc.splitTextToSize(termsText, pageWidth - (margin * 2) - summaryWidth - 10);
    doc.text(splitTerms, margin, y);
    y += splitTerms.length * 4 + 5;
  }

  // ============ SIGNATURE SECTION ============
  const signY = Math.max(y + 10, 230);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...primaryColor);
  doc.text('Customer Acceptance', margin, signY);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...mediumGray);
  doc.text('By signing below, you acknowledge acceptance of this quotation.', margin, signY + 5);

  // Signature lines
  const signLineY = signY + 12;
  
  // Signature line
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, signLineY, margin + 55, signLineY);
  doc.setFontSize(7);
  doc.setTextColor(...mediumGray);
  doc.text('Signature', margin, signLineY + 4);

  // Date line
  doc.line(margin + 65, signLineY, margin + 95, signLineY);
  doc.text('Date', margin + 65, signLineY + 4);

  // Print name line
  doc.line(margin + 105, signLineY, margin + 165, signLineY);
  doc.text('Print Name', margin + 105, signLineY + 4);

  // ============ FOOTER ============
  const footerY = pageHeight - 12;
  doc.setFillColor(...lightGray);
  doc.rect(0, footerY, pageWidth, 12, 'F');
  doc.setDrawColor(...borderColor);
  doc.line(0, footerY, pageWidth, footerY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...mediumGray);
  doc.text(`Quotation ${qNum} • Thank you for your business!`, pageWidth / 2, footerY + 6, { align: 'center' });
  doc.text(companyConfig.website, pageWidth - margin, footerY + 6, { align: 'right' });

  // ============ CONTACT INFO ============
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...mediumGray);
  const contactY = signY + 20;
  if (contactY < footerY - 15) {
    doc.text(`For questions: ${companyConfig.preparedBy} | ${companyConfig.phone} | ${companyConfig.email}`, 
      pageWidth / 2, contactY, { align: 'center' });
  }

  return doc;
};

// Helper function to convert hex to RGB
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [128, 128, 128];
}

  // Handle PDF Generation and Storage
  const handleGenerateAndStorePDF = async (quote) => {
    setGeneratingPdf(true);
    try {
      // Generate PDF
      const doc = generateProfessionalPDF(quote);
      const pdfBlob = doc.output('blob');
      
      // Create FormData for upload
      const formData = new FormData();
      const fileName = `${quote.quoteNumber || quote.id || 'quote'}.pdf`;
      formData.append('file1', pdfBlob, fileName);
      
      // Upload to server
      const quoteId = quote._id || quote.id;
      const response = await uploadQuotePdf(quoteId, formData);
      
      if (response.success) {
        toast.success('PDF generated and stored successfully!');
        // Get the PDF URL and show it
        const linkResponse = await getQuotePdfLink(quoteId);
        if (linkResponse.success && linkResponse.pdfUrl) {
          setPdfUrl(linkResponse.pdfUrl);
          setShowPdfViewer(true);
        }
        await fetchQuotes(); // Refresh to get updated quote data
      } else {
        toast.error('Failed to store PDF');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(error?.response?.data?.message || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Handle PDF View
  const handleViewPDF = async (quote) => {
    try {
      const quoteId = quote._id || quote.id;
      const response = await getQuotePdfLink(quoteId);
      
      if (response.success && response.pdfUrl) {
        setPdfUrl(response.pdfUrl);
        setShowPdfViewer(true);
      } else {
        // PDF doesn't exist, generate and store it
        await handleGenerateAndStorePDF(quote);
      }
    } catch (error) {
      console.error('Get PDF error:', error);
      if (error?.response?.status === 404) {
        // PDF not found, generate and store it
        await handleGenerateAndStorePDF(quote);
      } else {
        toast.error('Failed to load PDF');
      }
    }
  };

  // Get PDF share link
  const getPdfShareLink = async (quote) => {
    try {
      const quoteId = quote._id || quote.id;
      const response = await getQuotePdfLink(quoteId);
      
      if (response.success && response.pdfUrl) {
        return response.pdfUrl;
      }
      return null;
    } catch (error) {
      console.error('Get PDF link error:', error);
      return null;
    }
  };

  // Share Functions with PDF Link
  const handleShareWhatsApp = async () => {
    if (!shareQuoteItem) return;
    
    const q = shareQuoteItem;
    const cust = q.leadName || leadName || 'Customer';
    const comp = companyConfig.name;
    
    let pdfLink = '';
    try {
      const link = await getPdfShareLink(q);
      if (link) {
        pdfLink = `\n\n📄 View/Download PDF: ${link}`;
      }
    } catch (e) {
      console.log('No PDF available for sharing');
    }

    const cleanPhone = sharePhone.replace(/[^\d+]/g, '');

    const messageText =
      `Hello ${cust},\n` +
      `Thank you for your interest.\n\n` +
      `Please find your quotation attached. Kindly review it and let us know if you have any questions or require any changes.${pdfLink}\n\n` +
      `Thank you,\n` +
      `${comp}`;

    const encodedText = encodeURIComponent(messageText);
    const whatsappUrl = cleanPhone
      ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodedText}`
      : `https://api.whatsapp.com/send?text=${encodedText}`;

    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp to share quote...');
    setShowShareModal(false);
  };

  const getEmailContent = async (q) => {
    const qNum = q.quoteNumber || q.id || 'Quote';
    const cust = q.leadName || leadName || 'Customer';
    const comp = companyConfig.name;

    let pdfLink = '';
    try {
      const link = await getPdfShareLink(q);
      if (link) {
        pdfLink = `\n\nYou can view/download the PDF here: ${link}`;
      }
    } catch (e) {
      console.log('No PDF available for email');
    }

    const subject = `Quotation ${qNum} from ${comp}`;
    const body =
      `Dear ${cust},\n\n` +
      `Please find your quotation attached.${pdfLink}\n\n` +
      `Kindly review it and let us know if you have any questions or require any modifications.\n\n` +
      `We look forward to hearing from you.\n\n` +
      `Regards,\n` +
      `${comp}`;

    return { subject, body };
  };

  const handleShareGmailWeb = async () => {
    if (!shareQuoteItem) return;
    const { subject, body } = await getEmailContent(shareQuoteItem);

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(shareEmail)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
    toast.success('Opening Gmail Web Composer...');
    setShowShareModal(false);
  };

  const handleShareMailto = async () => {
    if (!shareQuoteItem) return;
    const { subject, body } = await getEmailContent(shareQuoteItem);

    const mailtoUrl = `mailto:${shareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, '_blank');
    toast.success('Opening default Mail Client...');
    setShowShareModal(false);
  };

  const handleEditQuote = (quote) => {
    if (quote.isActive === false) {
      toast.error('Inactive quotes are read-only history.');
      return;
    }

    setSelectedQuote(quote);
    const validUntilDate = quote.validUntil ? new Date(quote.validUntil).toISOString().substring(0, 10) : '';
    const deliveryDate = quote.deliveryDate ? new Date(quote.deliveryDate).toISOString().substring(0, 10) : '';
    
    const mappedItems = Array.isArray(quote.items) && quote.items.length > 0
      ? quote.items.map(i => ({
          description: i.description || '',
          qty: i.quantity || i.qty || 1,
          rate: i.unitPrice || i.rate || 0,
          tax: i.tax || 18
        }))
      : [{ description: '', qty: 1, rate: 0, tax: 18 }];

    setFormData({
      quoteNumber: quote.quoteNumber || '',
      leadId: quote.leadId || leadId || '',
      leadName: quote.leadName || leadName || '',
      project: quote.projectService || quote.project || quote.quoteTitle || '',
      amount: quote.grandTotal || quote.amount || 0,
      status: quote.status || 'Draft',
      validUntil: validUntilDate,
      items: mappedItems,
      terms: quote.termsAndConditions || quote.terms || '',
      discount: quote.discountValue || quote.discount || 0,
      billingAddress: quote.billingAddress || '',
      shippingAddress: quote.shippingAddress || '',
      poNumber: quote.poNumber || '',
      paymentTerms: quote.paymentTerms || 'Net 15',
      deliveryDate: deliveryDate
    });
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      quoteNumber: '',
      leadId: leadId || '',
      leadName: leadName || '',
      project: '',
      amount: 0,
      status: 'Draft',
      validUntil: '',
      items: [{ description: '', qty: 1, rate: 0, tax: 18 }],
      terms: '',
      discount: 0,
      billingAddress: '',
      shippingAddress: '',
      poNumber: '',
      paymentTerms: 'Net 15',
      deliveryDate: ''
    });
    setIsEditing(false);
    setSelectedQuote(null);
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', qty: 1, rate: 0, tax: 18 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) {
      toast.warning('At least one line item is required');
      return;
    }
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  const updateItem = (index, field, value) => {
    const updatedItems = formData.items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setFormData({ ...formData, items: updatedItems });
  };

  const getStatusBadge = (status) => {
    const color = STATUS_COLORS[status] || '#6B7280';
    const icon = STATUS_ICONS[status] || '📄';
    return (
      <span className="qut-status-badge" style={{ backgroundColor: color + '20', color: color, borderColor: color + '40' }}>
        {icon} {status}
      </span>
    );
  };

  const getActiveBadge = (isActive) => {
    const active = isActive !== false;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: 700,
          backgroundColor: active ? '#D1FAE5' : '#F3F4F6',
          color: active ? '#059669' : '#6B7280',
          border: active ? '1px solid #A7F3D0' : '1px solid #E5E7EB'
        }}
      >
        <span style={{ fontSize: '8px' }}>{active ? '🟢' : '⚪'}</span>
        {active ? 'Active' : 'Inactive'}
      </span>
    );
  };

  // PDF Viewer Modal Component
  const PdfViewer = () => {
    if (!showPdfViewer || !pdfUrl) return null;

    return (
      <div className="qut-modal-overlay" onClick={() => setShowPdfViewer(false)}>
        <div className="qut-modal-content" style={{ maxWidth: '90%', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
          <div className="qut-modal-header">
            <h3>📄 Quote PDF</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="qut-btn-secondary"
                onClick={() => window.open(pdfUrl, '_blank')}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <ExternalLink size={16} /> Open in New Tab
              </button>
              <button
                className="qut-btn-secondary"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = 'quote.pdf';
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Download size={16} /> Download
              </button>
              <button
                className="qut-modal-close"
                onClick={() => setShowPdfViewer(false)}
              >
                ×
              </button>
            </div>
          </div>
          <div className="qut-modal-body" style={{ padding: 0, height: '80vh' }}>
            <iframe
              src={pdfUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Quote PDF Viewer"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="lead-qut-container">
      {/* Header with actions */}
      <div className="qut-header">
        <div className="qut-header-left">
          <h3 className="qut-title">Quotes</h3>
          <span className="qut-count">{quotes.length} quotes</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="qut-btn-secondary"
            onClick={fetchQuotes}
            title="Refresh quotes list"
            style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Refresh
          </button>
          {!isClient && (
            <button
              className="qut-btn-primary"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              <Plus size={18} />
              New Quote
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="qut-table-container">
        {loading ? (
          <div className="qut-loading">Loading quotes...</div>
        ) : quotes.length === 0 ? (
          <div className="qut-empty-state">
            <FileText size={48} />
            <p>No quotes found</p>
            <p className="qut-empty-sub">Create your first quote for this lead</p>
          </div>
        ) : (
          <table className="qut-table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Lead ID</th>
                <th>Project</th>
                <th>Amount</th>
                <th>Active State</th>
                <th>Status</th>
                <th>Approver</th>
                <th>Valid Until</th>
                <th style={{ textAlign: 'right', paddingRight: '20px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((quote) => {
                const quoteId = quote._id || quote.id;
                const quoteNum = quote.quoteNumber || quote.id || '-';
                const currentLeadId = quote.leadId || leadId || '-';
                const projectDisplay = quote.projectService || quote.project || quote.quoteTitle || '-';
                const grandTotal = quote.grandTotal !== undefined ? quote.grandTotal : (quote.amount || 0);
                const isActive = quote.isActive !== false;
                const approverName = quote.approverName || (quote.approverId ? 'Assigned' : '-');
                const hasPdf = quote.pdfKey || false;

                return (
                  <tr key={quoteId} style={{ opacity: isActive ? 1 : 0.75, backgroundColor: isActive ? 'transparent' : '#FAFAFA' }}>
                    <td className="qut-quote-number">
                      {quoteNum}
                      {quote.revisionNumber > 0 && (
                        <span style={{ fontSize: '11px', color: '#7C3AED', marginLeft: '4px', fontWeight: 600 }}>
                          Rev.{quote.revisionNumber}
                        </span>
                      )}
                      {hasPdf && (
                        <span style={{ fontSize: '10px', color: '#059669', marginLeft: '4px', fontWeight: 600 }}>
                          📄
                        </span>
                      )}
                    </td>
                    <td style={{ fontWeight: 600, color: '#4B5563', fontSize: '12px' }}>{currentLeadId}</td>
                    <td>{projectDisplay}</td>
                    <td className="qut-amount">₹ {formatNum(grandTotal)}</td>
                    <td>{getActiveBadge(quote.isActive)}</td>
                    <td>{getStatusBadge(quote.status || 'Draft')}</td>
                    <td style={{ fontSize: '12px', color: quote.approverName ? '#7C3AED' : '#6B7280', fontWeight: quote.approverName ? 600 : 400 }}>
                      {approverName}
                    </td>
                    <td>{quote.validUntil ? formatDatePDF(quote.validUntil) : '-'}</td>
                    <td style={{ textAlign: 'right', paddingRight: '16px' }}>
                      <div className="qut-actions" style={{ justifyContent: 'flex-end', gap: '6px' }}>
                        {/* View Action - ALWAYS Available */}
                        <button
                          className="qut-btn-icon qut-btn-view"
                          onClick={() => handleViewQuote(quote)}
                          title="View Quote"
                        >
                          <Eye size={16} />
                        </button>

                        {/* Single PDF Button - Generate/View */}
                        <button
                          className="qut-btn-icon"
                          style={{ 
                            color: hasPdf ? '#059669' : '#DC2626', 
                            background: hasPdf ? '#D1FAE5' : '#FEE2E2' 
                          }}
                          onClick={() => handleViewPDF(quote)}
                          title={hasPdf ? "View PDF" : "Generate PDF"}
                          disabled={generatingPdf}
                        >
                          {generatingPdf ? (
                            <RefreshCw size={16} className="spin" />
                          ) : (
                            <FileText size={16} />
                          )}
                        </button>

                        {/* Actions ONLY Available for ACTIVE Quotes */}
                        {isActive && (
                          <>
                            {/* Share Action */}
                            <button
                              className="qut-btn-icon"
                              style={{ color: '#25D366', background: '#DCFCE7' }}
                              onClick={() => handleOpenShareModal(quote)}
                              title="Share Quote (WhatsApp / Email / PDF)"
                            >
                              <Share2 size={16} />
                            </button>

                            {/* Send to Approver Action (Owner only) */}
                            {!isClient && (
                              <button
                                className="qut-btn-icon"
                                style={{ color: '#D97706', background: '#FEF3C7' }}
                                onClick={() => handleOpenApproverModal(quote)}
                                title="Send to Approver"
                              >
                                <Send size={16} />
                              </button>
                            )}

                            {/* Edit Action (Owner only) */}
                            {!isClient && (
                              <button
                                className="qut-btn-icon qut-btn-edit"
                                onClick={() => handleEditQuote(quote)}
                                title="Edit Quote Status & Details"
                              >
                                <Edit size={16} />
                              </button>
                            )}

                            {/* Revise Action */}
                            {!isClient && quote.status === 'Rejected' && (
                              <button
                                className="qut-btn-icon"
                                style={{ color: '#059669', background: '#D1FAE5' }}
                                onClick={() => handleRevise(quoteId)}
                                title="Revise Quote"
                              >
                                <RefreshCw size={16} />
                              </button>
                            )}

                            {/* Delete Action */}
                            {!isClient && quote.status === 'Draft' && (
                              <button
                                className="qut-btn-icon qut-btn-delete"
                                onClick={() => handleDeleteQuote(quoteId, isActive)}
                                title="Delete Quote"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="qut-modal-overlay" onClick={() => {
          if (!isEditing && !submitting) {
            resetForm();
            setShowCreateModal(false);
          }
        }}>
          <div className="qut-modal-content" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
            <div className="qut-modal-header">
              <h3>{isEditing ? 'Edit Active Quote' : 'Create New Quote'}</h3>
              <button
                className="qut-modal-close"
                onClick={() => {
                  resetForm();
                  setShowCreateModal(false);
                }}
              >
                ×
              </button>
            </div>

            <div className="qut-modal-body">
              {/* Quote Details */}
              <div className="qut-form-section">
                <h4>Quote Details</h4>
                <div className="qut-form-grid">
                  <div className="qut-form-group">
                    <label>Lead ID</label>
                    <input
                      type="text"
                      value={formData.leadId || leadId || ''}
                      disabled
                      style={{ background: '#F3F4F6', color: '#4B5563', fontWeight: 600 }}
                    />
                  </div>
                  <div className="qut-form-group">
                    <label>Valid Until *</label>
                    <input
                      type="date"
                      value={formData.validUntil}
                      min={new Date().toISOString().substring(0, 10)}
                      onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                      required
                    />
                  </div>
                  <div className="qut-form-group">
                    <label>Project / Service *</label>
                    <input
                      type="text"
                      value={formData.project}
                      onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                      placeholder="e.g., Website Development"
                      required
                    />
                  </div>
                  <div className="qut-form-group">
                    <label>Quote Status (Owner Control)</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Sent">Sent</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Expired">Expired</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="qut-form-group qut-form-group-full">
                    <label>Customer Name</label>
                    <input
                      type="text"
                      value={formData.leadName || leadName || ''}
                      onChange={(e) => setFormData({ ...formData, leadName: e.target.value })}
                      placeholder="Customer name"
                      disabled={!!leadId}
                    />
                  </div>
                </div>
              </div>

             

              {/* Line Items */}
              <div className="qut-form-section">
                <div className="qut-section-header">
                  <h4>Line Items</h4>
                  <button type="button" className="qut-btn-soft" onClick={addItem}>
                    <Plus size={16} /> Add Item
                  </button>
                </div>
                <div className="qut-items-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Description *</th>
                        <th style={{ width: '80px' }}>Qty</th>
                        <th style={{ width: '120px' }}>Rate (₹)</th>
                        <th style={{ width: '80px' }}>Tax %</th>
                        <th style={{ width: '120px' }}>Amount</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.items.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                              placeholder="Item description"
                            />
                          </td>
                          <td>
  <input
    type="number"
    value={item.qty}
    onChange={(e) =>
      updateItem(
        index,
        'qty',
        e.target.value === '' ? '' : Number(e.target.value)
      )
    }
    onBlur={(e) => {
      if (e.target.value === '') {
        updateItem(index, 'qty', 0);
      }
    }}
    min="1"
  />
</td>
                          <td>
  <input
    type="number"
    value={item.rate}
    onChange={(e) =>
      updateItem(
        index,
        'rate',
        e.target.value === '' ? '' : Number(e.target.value)
      )
    }
    onBlur={(e) => {
      if (e.target.value === '') {
        updateItem(index, 'rate', 0);
      }
    }}
    min="0"
  />
</td>
                          <td>
  <input
    type="number"
    value={item.tax}
    onChange={(e) =>
      updateItem(
        index,
        'tax',
        e.target.value === '' ? '' : Number(e.target.value)
      )
    }
    onBlur={(e) => {
      if (e.target.value === '') {
        updateItem(index, 'tax', 0);
      }
    }}
    min="0"
  />
</td>
                          <td className="qut-item-amount">
                            ₹ {formatNum(Number(item.qty) * Number(item.rate))}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="qut-btn-icon qut-btn-delete"
                              onClick={() => removeItem(index)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Discount & Terms */}
              <div className="qut-form-section">
                <div className="qut-form-grid">
                  <div className="qut-form-group">
                    <label>Discount (%)</label>
                   <input
  type="number"
  value={formData.discount}
  onChange={(e) =>
    setFormData({
      ...formData,
      discount: e.target.value === '' ? '' : Number(e.target.value)
    })
  }
  onBlur={(e) => {
    if (e.target.value === '') {
      setFormData({
        ...formData,
        discount: 0
      });
    }
  }}
  min="0"
  max="100"
/>
                  </div>
                  <div className="qut-form-group qut-form-group-full">
                    <label>Terms & Conditions</label>
                    <textarea
                      value={formData.terms}
                      onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                      rows="3"
                      placeholder="Enter terms and conditions..."
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="qut-summary">
                <div className="qut-summary-row">
                  <span>Subtotal</span>
                  <span>₹ {formatNum(totals.subtotal)}</span>
                </div>
                {formData.discount > 0 && (
                  <div className="qut-summary-row">
                    <span>Discount ({formData.discount}%)</span>
                    <span className="qut-discount">-₹ {formatNum(totals.discountAmount)}</span>
                  </div>
                )}
                <div className="qut-summary-row">
                  <span>Tax (GST)</span>
                  <span>₹ {formatNum(totals.tax)}</span>
                </div>
                <div className="qut-summary-row qut-summary-total">
                  <span>Grand Total</span>
                  <span>₹ {formatNum(totals.total)}</span>
                </div>
              </div>
            </div>

            <div className="qut-modal-footer">
              <button
                className="qut-btn-secondary"
                disabled={submitting}
                onClick={() => {
                  resetForm();
                  setShowCreateModal(false);
                }}
              >
                Cancel
              </button>
              <button
                className="qut-btn-primary"
                disabled={submitting}
                onClick={isEditing ? handleUpdateQuote : handleCreateQuote}
              >
                {submitting ? 'Saving...' : isEditing ? 'Update Quote' : 'Create Quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send to Approver Modal */}
      {showApproverModal && approverQuoteItem && (
        <div className="qut-modal-overlay" onClick={() => setShowApproverModal(false)}>
          <div className="qut-modal-content" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="qut-modal-header">
              <h3>✉️ Send Quote to Approver</h3>
              <button className="qut-modal-close" onClick={() => setShowApproverModal(false)}>
                ×
              </button>
            </div>

            <div className="qut-modal-body">
              <div style={{ background: '#FEF3C7', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #FDE68A' }}>
                <div style={{ fontWeight: 600, color: '#92400E', fontSize: '14px' }}>
                  {approverQuoteItem.quoteNumber || approverQuoteItem.id} — {approverQuoteItem.projectService || approverQuoteItem.project}
                </div>
                <div style={{ fontSize: '13px', color: '#B45309', marginTop: '2px' }}>
                  Lead ID: <strong>{approverQuoteItem.leadId || leadId}</strong> • Amount: <strong>₹ {formatNum(approverQuoteItem.grandTotal || approverQuoteItem.amount || 0)}</strong>
                </div>
              </div>

              {/* Autocomplete Input */}
              <div className="qut-form-group" style={{ position: 'relative', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
                  <UserCheck size={16} color="#7C3AED" /> Search Approver (Email, Username, or Phone) *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Type email, username or phone number..."
                    value={approverQuery}
                    onChange={(e) => {
                      setApproverQuery(e.target.value);
                      setSelectedCandidate(null);
                    }}
                    style={{ paddingRight: '36px' }}
                  />
                  <Search size={16} style={{ position: 'absolute', right: '12px', top: '12px', color: '#9CA3AF' }} />
                </div>

                {/* Autocomplete Suggestions Dropdown */}
                {searchingApprovers && (
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>Searching users in tenant...</div>
                )}

                {approverSuggestions.length > 0 && !selectedCandidate && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      marginTop: '4px'
                    }}
                  >
                    {approverSuggestions.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => {
                          setSelectedCandidate(user);
                          setApproverQuery(`${user.name} (${user.email || user.username})`);
                        }}
                        style={{
                          padding: '10px 14px',
                          borderBottom: '1px solid #F3F4F6',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px'
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#FFFFFF')}
                      >
                        <div style={{ fontWeight: 600, fontSize: '13px', color: '#111827' }}>{user.name}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          Email: {user.email || 'N/A'} {user.phone ? `• Phone: ${user.phone}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchAttempted && !searchingApprovers && approverSuggestions.length === 0 && !selectedCandidate && approverQuery.trim().length > 0 && (
                  <div style={{ color: '#EF4444', fontSize: '13px', marginTop: '8px', fontWeight: 600 }}>
                    ⚠️ No user found in this tenant.
                  </div>
                )}
              </div>

              {/* Selected Candidate Preview Card */}
              {selectedCandidate && (
                <div
                  style={{
                    background: '#ECFDF5',
                    border: '1px solid #A7F3D0',
                    borderRadius: '8px',
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: '#065F46', fontSize: '14px' }}>
                      Selected Approver: {selectedCandidate.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#047857', marginTop: '2px' }}>
                      {selectedCandidate.email ? `Email: ${selectedCandidate.email}` : ''} {selectedCandidate.phone ? `• Phone: ${selectedCandidate.phone}` : ''}
                    </div>
                  </div>
                  <button
                    className="qut-btn-secondary"
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                    onClick={() => {
                      setSelectedCandidate(null);
                      setApproverQuery('');
                    }}
                  >
                    Change
                  </button>
                </div>
              )}
            </div>

            <div className="qut-modal-footer">
              <button className="qut-btn-secondary" onClick={() => setShowApproverModal(false)}>
                Cancel
              </button>
              <button
                className="qut-btn-primary"
                disabled={!selectedCandidate || submitting}
                style={{ background: '#D97706' }}
                onClick={handleSendToApproverSubmit}
              >
                {submitting ? 'Sending...' : 'Confirm & Send for Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Quote Modal */}
      {showShareModal && shareQuoteItem && (
        <div className="qut-modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="qut-modal-content" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="qut-modal-header">
              <h3>📲 Share Quotation ({shareQuoteItem.quoteNumber || shareQuoteItem.id})</h3>
              <button className="qut-modal-close" onClick={() => setShowShareModal(false)}>
                ×
              </button>
            </div>

            <div className="qut-modal-body">
              <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #E5E7EB' }}>
                <div style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>
                  {shareQuoteItem.projectService || shareQuoteItem.project || shareQuoteItem.quoteTitle}
                </div>
                <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '2px' }}>
                  Customer: <strong>{shareQuoteItem.leadName || leadName || 'Customer'}</strong> • Amount: <strong>₹ {formatNum(shareQuoteItem.grandTotal || shareQuoteItem.amount || 0)}</strong>
                </div>
                {shareQuoteItem.pdfKey && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FileText size={14} /> PDF is uploaded and ready to share
                  </div>
                )}
              </div>

              {/* Phone for WhatsApp */}
              <div className="qut-form-group" style={{ marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Phone Number (for WhatsApp Share)
                </label>
                <input
                  type="text"
                  placeholder="+91 9876543210"
                  value={sharePhone}
                  onChange={(e) => setSharePhone(e.target.value)}
                />
              </div>

              {/* Email for Email Share */}
              <div className="qut-form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Email Address (for Email Share)
                </label>
                <input
                  type="email"
                  placeholder="customer@example.com"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                />
              </div>

              {/* Share Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleShareWhatsApp}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    background: '#25D366',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <MessageSquare size={18} />
                  Share via WhatsApp
                </button>

                <button
                  type="button"
                  onClick={handleShareGmailWeb}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    background: '#EA4335',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <Mail size={18} />
                  Open in Gmail Web
                </button>

                <button
                  type="button"
                  onClick={handleShareMailto}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    background: '#3B82F6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <Mail size={18} />
                  Open System Mail App
                </button>
              </div>
            </div>

            <div className="qut-modal-footer">
              <button className="qut-btn-secondary" onClick={() => setShowShareModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {showDetailModal && selectedQuote && (
        <div className="qut-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="qut-modal-content qut-detail-modal" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div className="qut-modal-header">
              <h3>Quote Details</h3>
              <button
                className="qut-modal-close"
                onClick={() => setShowDetailModal(false)}
              >
                ×
              </button>
            </div>

            <div className="qut-modal-body">
              <div className="qut-detail-header">
                <div>
                  <h2>{selectedQuote.quoteNumber || selectedQuote.id}</h2>
                  <p>{selectedQuote.projectService || selectedQuote.project || selectedQuote.quoteTitle}</p>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {getActiveBadge(selectedQuote.isActive)}
                  {getStatusBadge(selectedQuote.status || 'Draft')}
                </div>
              </div>

              <div className="qut-detail-grid">
                <div>
                  <label>Customer</label>
                  <p>{selectedQuote.leadName || leadName || 'N/A'}</p>
                </div>
                <div>
                  <label>Assigned Approver</label>
                  <p style={{ color: selectedQuote.approverName ? '#7C3AED' : '#6B7280', fontWeight: 600 }}>
                    {selectedQuote.approverName || 'Not Assigned'}
                  </p>
                </div>
                <div>
                  <label>Valid Until</label>
                  <p>{selectedQuote.validUntil ? formatDatePDF(selectedQuote.validUntil) : '-'}</p>
                </div>
                <div>
                  <label>Created Date</label>
                  <p>{selectedQuote.createdAt ? formatDatePDF(selectedQuote.createdAt) : '-'}</p>
                </div>
                {selectedQuote.billingAddress && (
                  <div>
                    <label>Billing Address</label>
                    <p>{selectedQuote.billingAddress}</p>
                  </div>
                )}
                {selectedQuote.shippingAddress && (
                  <div>
                    <label>Shipping Address</label>
                    <p>{selectedQuote.shippingAddress}</p>
                  </div>
                )}
                {selectedQuote.poNumber && (
                  <div>
                    <label>PO Number</label>
                    <p>{selectedQuote.poNumber}</p>
                  </div>
                )}
                {selectedQuote.paymentTerms && (
                  <div>
                    <label>Payment Terms</label>
                    <p>{selectedQuote.paymentTerms}</p>
                  </div>
                )}
                {selectedQuote.deliveryDate && (
                  <div>
                    <label>Delivery Date</label>
                    <p>{formatDatePDF(selectedQuote.deliveryDate)}</p>
                  </div>
                )}
              </div>

              <div className="qut-detail-items">
                <h4>Line Items</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Tax</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedQuote.items && selectedQuote.items.map((item, index) => {
                      const qty = item.quantity || item.qty || 1;
                      const rate = item.unitPrice || item.rate || 0;
                      return (
                        <tr key={index}>
                          <td>{item.description}</td>
                          <td>{qty}</td>
                          <td>₹ {formatNum(rate)}</td>
                          <td>{item.tax || 18}%</td>
                          <td>₹ {formatNum(Number(qty) * Number(rate))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="qut-detail-summary">
                <div className="qut-summary-row">
                  <span>Subtotal</span>
                  <span>₹ {formatNum(selectedQuote.subtotal || selectedQuote.amount || 0)}</span>
                </div>
                {(selectedQuote.discountValue || selectedQuote.discount) > 0 && (
                  <div className="qut-summary-row">
                    <span>Discount</span>
                    <span className="qut-discount">-₹ {formatNum(selectedQuote.discountAmount || 0)}</span>
                  </div>
                )}
                <div className="qut-summary-row">
                  <span>Tax (GST)</span>
                  <span>₹ {formatNum(selectedQuote.taxAmount || 0)}</span>
                </div>
                <div className="qut-summary-row qut-summary-total">
                  <span>Grand Total</span>
                  <span>₹ {formatNum(selectedQuote.grandTotal || selectedQuote.amount || 0)}</span>
                </div>
              </div>

              {(selectedQuote.termsAndConditions || selectedQuote.terms) && (
                <div className="qut-detail-terms" style={{ marginTop: '12px' }}>
                  <h4>Terms & Conditions</h4>
                  <p>{selectedQuote.termsAndConditions || selectedQuote.terms}</p>
                </div>
              )}
            </div>

            <div className="qut-modal-footer">
              <button
                className="qut-btn-secondary"
                onClick={() => setShowDetailModal(false)}
              >
                Close
              </button>
              <button
                className="qut-btn-primary"
                onClick={() => handleViewPDF(selectedQuote)}
                disabled={generatingPdf}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                {generatingPdf ? (
                  <RefreshCw size={16} className="spin" />
                ) : (
                  <FileText size={16} />
                )}
                {selectedQuote.pdfKey ? 'View PDF' : 'Generate PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      <PdfViewer />
    </div>
  );
}

export default QuotesTab;